import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { Percent, Users, SlidersHorizontal } from 'lucide-react';

interface AgenteComissao {
  agenteId: string; nome: string; email: string | null;
  comissaoEfetiva: number; override: number | null;
}
interface Resumo {
  global: number; agentesAtivos: number; overridesAtivos: number; items: AgenteComissao[];
}

export function ComissoesPage() {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');

  const [globalPct, setGlobalPct] = useState('');
  const [globalMotivo, setGlobalMotivo] = useState('');

  const [ovAgentes, setOvAgentes] = useState<string[]>([]);
  const [ovPct, setOvPct] = useState('');
  const [ovMotivo, setOvMotivo] = useState('');
  const [removendoMotivo, setRemovendoMotivo] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['comissoes'],
    queryFn: () => api<Resumo>('/comissoes'),
  });

  const aplicarGlobal = useMutation({
    mutationFn: () => api('/comissoes/global', { method: 'POST', body: { percentual: Number(globalPct.replace(',', '.')), motivo: globalMotivo || undefined } }),
    onSuccess: () => { setGlobalPct(''); setGlobalMotivo(''); setErro(null); qc.invalidateQueries({ queryKey: ['comissoes'] }); },
    onError: (e: any) => setErro(e?.apiError?.message ?? 'Falha ao aplicar regra global'),
  });

  const aplicarOverride = useMutation({
    mutationFn: () => api('/comissoes/override', { method: 'POST', body: { agenteIds: ovAgentes, percentual: Number(ovPct.replace(',', '.')), motivo: ovMotivo || undefined } }),
    onSuccess: () => { setOvAgentes([]); setOvPct(''); setOvMotivo(''); setErro(null); qc.invalidateQueries({ queryKey: ['comissoes'] }); },
    onError: (e: any) => setErro(e?.apiError?.message ?? 'Falha ao aplicar override'),
  });

  const removerOverride = useMutation({
    mutationFn: (agenteId: string) => api(`/comissoes/override/${agenteId}`, { method: 'DELETE', body: { motivo: removendoMotivo[agenteId] || undefined } }),
    onSuccess: () => { setErro(null); qc.invalidateQueries({ queryKey: ['comissoes'] }); },
    onError: (e: any) => setErro(e?.apiError?.message ?? 'Falha ao remover override'),
  });

  const itens = (data?.items ?? []).filter((a) => {
    if (!busca) return true;
    const b = busca.toLowerCase();
    return a.nome.toLowerCase().includes(b) || (a.email ?? '').toLowerCase().includes(b);
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Comissões</h1>
        <p className="text-sm text-muted-foreground">Ajuste o percentual global e aplique overrides individuais por agente. A comissão incide sobre o valor do auxílio emergencial.</p>
      </div>

      {erro && <div className="mb-4 rounded bg-red-50 border border-red-200 p-3 text-sm text-red-800">{erro} <button className="ml-2 underline" onClick={() => setErro(null)}>fechar</button></div>}

      {/* cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-xs text-muted-foreground">Comissão global</p><p className="text-2xl font-bold">{data ? `${data.global.toFixed(2)}%` : '—'}</p><p className="text-xs text-muted-foreground mt-1">Percentual padrão vigente</p></div>
          <Percent className="h-5 w-5 text-primary" /></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-xs text-muted-foreground">Agentes ativos</p><p className="text-2xl font-bold">{data?.agentesAtivos ?? '—'}</p><p className="text-xs text-muted-foreground mt-1">Elegíveis à regra global ou override</p></div>
          <Users className="h-5 w-5 text-primary" /></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-xs text-muted-foreground">Overrides ativos</p><p className="text-2xl font-bold">{data?.overridesAtivos ?? '—'}</p><p className="text-xs text-muted-foreground mt-1">Sobrescrevendo o percentual global</p></div>
          <SlidersHorizontal className="h-5 w-5 text-primary" /></CardContent></Card>
      </div>

      {/* formulários lado a lado */}
      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Regra global</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Aplica prospectivamente a novos cadastros e renovações sem override individual.</p>
            <div>
              <label className="text-sm font-medium">Percentual global (%)</label>
              <Input type="text" placeholder="10.00" value={globalPct} onChange={(e) => setGlobalPct(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium">Motivo</label>
              <textarea className="w-full rounded border p-2 text-sm" rows={2} placeholder="Motivo da alteração global" value={globalMotivo} onChange={(e) => setGlobalMotivo(e.target.value)} />
            </div>
            <Button disabled={!globalPct || aplicarGlobal.isPending} onClick={() => aplicarGlobal.mutate()}>Aplicar regra global</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Override por agente</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">O override individual prevalece sobre a regra global enquanto estiver ativo.</p>
            <div>
              <label className="text-sm font-medium">Agentes</label>
              <select multiple className="w-full rounded border p-2 text-sm h-24" value={ovAgentes}
                onChange={(e) => setOvAgentes(Array.from(e.target.selectedOptions, (o) => o.value))}>
                {(data?.items ?? []).map((a) => <option key={a.agenteId} value={a.agenteId}>{a.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Percentual override (%)</label>
              <Input type="text" placeholder="12.50" value={ovPct} onChange={(e) => setOvPct(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium">Motivo</label>
              <textarea className="w-full rounded border p-2 text-sm" rows={2} placeholder="Motivo do override" value={ovMotivo} onChange={(e) => setOvMotivo(e.target.value)} />
            </div>
            <Button disabled={ovAgentes.length === 0 || !ovPct || aplicarOverride.isPending} onClick={() => aplicarOverride.mutate()}>Aplicar override</Button>
          </CardContent>
        </Card>
      </div>

      {/* tabela de agentes */}
      <Card>
        <CardHeader><CardTitle>Agentes e comissão efetiva</CardTitle></CardHeader>
        <CardContent>
          <Input placeholder="Buscar por nome ou e-mail" value={busca} onChange={(e) => setBusca(e.target.value)} className="mb-3 max-w-md" />
          {isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500">
                <th className="py-2">Agente</th><th className="py-2 text-center">Comissão efetiva</th>
                <th className="py-2 text-center">Override</th><th className="py-2">Ações</th>
              </tr></thead>
              <tbody>
                {itens.map((a) => (
                  <tr key={a.agenteId} className="border-b">
                    <td className="py-2">{a.nome}<br /><span className="text-xs text-gray-500">{a.email}</span></td>
                    <td className="py-2 text-center font-semibold">{a.comissaoEfetiva.toFixed(2)}%</td>
                    <td className="py-2 text-center">{a.override !== null ? `${a.override.toFixed(2)}%` : <span className="text-gray-400">Global</span>}</td>
                    <td className="py-2">
                      {a.override !== null ? (
                        <div className="flex gap-2 items-center">
                          <Input placeholder="Motivo" className="h-8 text-xs" value={removendoMotivo[a.agenteId] ?? ''} onChange={(e) => setRemovendoMotivo((m) => ({ ...m, [a.agenteId]: e.target.value }))} />
                          <Button size="sm" variant="outline" disabled={removerOverride.isPending} onClick={() => removerOverride.mutate(a.agenteId)}>Remover</Button>
                        </div>
                      ) : <span className="text-xs text-gray-400">Sem override ativo.</span>}
                    </td>
                  </tr>
                ))}
                {itens.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-400">Nenhum agente encontrado.</td></tr>}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
